import { apiBaseUrl } from "./auth";
import { getStoredLanguage } from "../context/I18nContext";

const LESSONS_BASE = `${apiBaseUrl}/api/lessons`;

async function request(
  path,
  { accessToken, method = "GET", body, params } = {},
) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": getStoredLanguage(),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const queryParams = params
    ? { ...params, lang: getStoredLanguage() }
    : { lang: getStoredLanguage() };
  const queryString = `?${new URLSearchParams(queryParams).toString()}`;

  const response = await fetch(`${LESSONS_BASE}${path}${queryString}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || "Une erreur est survenue";
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function fetchProgramTests({ accessToken, params } = {}) {
  return request("/program-tests/", { accessToken, params });
}

export function fetchProgramTest({ accessToken, testId }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/`, { accessToken });
}

export function createProgramTest({ accessToken, payload }) {
  return request("/program-tests/", {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function updateProgramTest({ accessToken, testId, payload }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function deleteProgramTest({ accessToken, testId }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/`, {
    accessToken,
    method: "DELETE",
  });
}

export function createProgramTestExercise({ accessToken, testId, payload }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/exercises/`, {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function updateProgramTestExercise({
  accessToken,
  exerciseId,
  payload,
}) {
  if (!exerciseId) {
    throw new Error("exerciseId is required");
  }
  return request(`/program-test-exercises/${exerciseId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function deleteProgramTestExercise({ accessToken, exerciseId }) {
  if (!exerciseId) {
    throw new Error("exerciseId is required");
  }
  return request(`/program-test-exercises/${exerciseId}/`, {
    accessToken,
    method: "DELETE",
  });
}

export function submitProgramTest({ accessToken, testId, payload }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/submit/`, {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function submitProgramTestPublic({ testId, payload }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/public-submit/`, {
    method: "POST",
    body: payload,
  });
}

export function fetchProgramTestSubmissions({ accessToken, testId }) {
  if (!testId) {
    throw new Error("testId is required");
  }
  return request(`/program-tests/${testId}/submissions/`, {
    accessToken,
    method: "GET",
  });
}

export function updateProgramTestSubmission({
  accessToken,
  submissionId,
  payload,
}) {
  if (!submissionId) {
    throw new Error("submissionId is required");
  }
  return request(`/program-test-submissions/${submissionId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function updateProgramTestLeadSubmission({
  accessToken,
  submissionId,
  payload,
}) {
  if (!submissionId) {
    throw new Error("submissionId is required");
  }
  return request(`/program-test-lead-submissions/${submissionId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function normalizeTestType(raw) {
  const v = String(raw || "").trim();
  const allowed = new Set([
    "text_comprehension",
    "grammar",
    "vocabulary",
    "math_problems",
    "calculations",
    "qcm",
    "problem_solving",
    "data_interpretation",
    "written_expression",
    "mixed_test",
  ]);
  if (allowed.has(v)) return v;
  return "mixed_test";
}

export function buildDefaultExerciseConfig(type) {
  const t = String(type || "").trim();
  if (t === "multiple_choice") {
    return {
      allow_multiple: false,
      ui: "list",
      options: [
        { id: "A", text: "" },
        { id: "B", text: "" },
        { id: "C", text: "" },
      ],
    };
  }
  if (t === "fill_blanks") {
    return {
      template: "Complète: Je {{1}} à l'école.",
      blanks: [{ id: "1", placeholder: "..." }],
    };
  }
  if (t === "long_text") return { rows: 8 };
  if (t === "task") return { rows: 10 };
  if (t === "essay") return { rows: 10 };
  if (t === "short_text") return { placeholder: "" };
  if (t === "stimulus") return {};
  if (t === "data_interpretation") {
    return { title: "", prompt: "", items: [], questions: [] };
  }
  return {};
}

export function scaffoldExercisesForTestType(testType) {
  const t = normalizeTestType(testType);

  if (t === "text_comprehension") {
    return [
      {
        exercise_type: "stimulus",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("stimulus"),
      },
    ];
  }

  if (t === "written_expression") {
    return [
      {
        exercise_type: "long_text",
        prompt: "",
        points: 10,
        config: { ...buildDefaultExerciseConfig("long_text"), rows: 14 },
      },
    ];
  }

  if (t === "qcm") {
    return [
      {
        exercise_type: "multiple_choice",
        prompt: "",
        points: 1,
        config: {
          ...buildDefaultExerciseConfig("multiple_choice"),
          allow_multiple: false,
        },
      },
    ];
  }

  if (t === "data_interpretation") {
    return [
      {
        exercise_type: "data_interpretation",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("data_interpretation"),
      },
    ];
  }

  if (t === "calculations") {
    return [
      {
        exercise_type: "short_text",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("short_text"),
      },
    ];
  }

  if (t === "math_problems" || t === "problem_solving") {
    return [
      {
        exercise_type: "stimulus",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("stimulus"),
      },
      {
        exercise_type: "short_text",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("short_text"),
      },
    ];
  }

  if (t === "grammar" || t === "vocabulary") {
    return [
      {
        exercise_type: "multiple_choice",
        prompt: "",
        points: 1,
        config: buildDefaultExerciseConfig("multiple_choice"),
      },
    ];
  }

  return [];
}

export async function createScaffoldExercises({
  accessToken,
  testId,
  testType,
  startOrder = 0,
} = {}) {
  if (!testId) throw new Error("testId is required");

  const list = scaffoldExercisesForTestType(testType);
  if (!list.length) return [];

  const created = [];
  for (let i = 0; i < list.length; i += 1) {
    const payload = { ...list[i], order: startOrder + i };
    // eslint-disable-next-line no-await-in-loop
    const ex = await createProgramTestExercise({
      accessToken,
      testId,
      payload,
    });
    created.push(ex);
  }
  return created;
}
